import React, { useState, useRef } from "react";
import { Send, Image, Video, X, Reply } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useChatStore } from "@/store/chat";
import { ChatMessage } from "@/types/chat";
import { cn } from "@/lib/currencyFormatter";
import { toast } from "@/hooks/use-toast";
import { uploadChatAttachment } from "@/services/chat/attachmentService";
import { trackEvent } from "@/services/analytics";

interface ChatMessageInputProps {
  replyTo: ChatMessage | null;
  onCancelReply: () => void;
  onSendMessage: (
    content: string,
    messageType: "text" | "image" | "video" | "system",
    attachmentUrl?: string,
    replyTo?: string
  ) => Promise<void>;
}

export const ChatMessageInput: React.FC<ChatMessageInputProps> = ({
  replyTo,
  onCancelReply,
  onSendMessage,
}) => {
  const [message, setMessage] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { currentStoreId } = useChatStore();

  const handleSendMessage = async () => {
    if (!message.trim()) return;

    try {
      await onSendMessage(message, "text", undefined, replyTo?.id);
      setMessage("");

      // Focus back on textarea
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "image" | "video"
  ) => {
    const file = e.target.files?.[0];
    if (!file || !currentStoreId) return;

    setIsUploading(true);

    try {
      // Upload to Supabase storage and get public URL for the attachment
      const publicUrl = await uploadChatAttachment(file, currentStoreId);

      // Track event
      trackEvent("chat_attachment_sent", {
        attachment_type: type,
        file_size: file.size,
        store_id: currentStoreId,
      });

      // Send message with attachment
      await onSendMessage(message, type, publicUrl, replyTo?.id);
      setMessage("");

      toast({
        title: "Attachment sent",
        description: `Your ${type} has been uploaded and sent.`,
      });
    } catch (error) {
      console.error("Upload error:", error);
      toast({
        title: "Upload failed",
        description: "Failed to upload attachment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);

      // Reset file inputs
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      if (videoInputRef.current) {
        videoInputRef.current.value = "";
      }
    }
  };

  return (
    <div className="border-t p-2 bg-white dark:bg-gray-950">
      {replyTo && (
        <div className="flex items-center mb-2 p-1.5 bg-secondary/30 rounded-md">
          <Reply className="h-4 w-4 mr-1" />
          <div className="flex-1 text-sm truncate">
            <span className="font-semibold">
              Reply to {replyTo.sender_name}:{" "}
            </span>
            {replyTo.message_type === "text"
              ? replyTo.content.substring(0, 50) +
                (replyTo.content.length > 50 ? "..." : "")
              : `${replyTo.message_type} attachment`}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={onCancelReply}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      <div className="flex flex-row items-center gap-2 rounded-md border bg-background shadow-sm">
        <div className="flex-1 relative">
          <Textarea
            ref={textareaRef}
            placeholder="Type a message..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            className={cn(
              "min-h-[20px] max-h-[80px] w-full resize-none border-0 py-1.5 px-2 focus:ring-0",
              "focus:outline-none focus-visible:ring-0 bg-transparent text-sm"
            )}
            style={{ lineHeight: "1.3", minHeight: "38px", height: "38px" }}
            disabled={isUploading}
          />

          <div className="flex items-center">
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              onChange={(e) => handleFileUpload(e, "image")}
              className="hidden"
              id="image-upload"
            />
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 rounded-full"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              title="Upload Image"
            >
              <Image className="h-4 w-4 text-muted-foreground" />
            </Button>

            <input
              type="file"
              ref={videoInputRef}
              accept="video/*"
              onChange={(e) => handleFileUpload(e, "video")}
              className="hidden"
              id="video-upload"
            />
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 rounded-full"
              onClick={() => videoInputRef.current?.click()}
              disabled={isUploading}
              title="Upload Video"
            >
              <Video className="h-4 w-4 text-muted-foreground" />
            </Button>
          </div>
        </div>

        <Button
          size="sm"
          className="m-1 h-7 w-7 rounded-full bg-primary hover:bg-primary/90 flex items-center justify-center"
          onClick={handleSendMessage}
          disabled={(!message.trim() && !isUploading) || isUploading}
        >
          <Send className="h-3.5 w-3.5" />
        </Button>
      </div>

      {isUploading && (
        <div className="text-xs text-center mt-1 text-muted-foreground">
          Uploading... Please wait
        </div>
      )}
    </div>
  );
};
